// Quem pode operar a cobrança de uma fatura de empresa parceira (criar, cancelar,
// prorrogar, montar o PDF combinado). As edge functions rodam com service role e
// ignoram o RLS, então a regra precisa existir aqui em código também.
//
// Espelha a policy `ep_faturamento_financeiro_select` da migration
// 20260810200603: o time INTERNO de Faturamento/Financeiro fatura em nome da
// Vigor e por isso enxerga e opera as parceiras. Até 11/08/2026 estas functions
// só aceitavam `adm` ou usuário da própria parceira — a analista de Faturamento
// levava 403 "Forbidden" ao emitir o boleto da Gran (fatura 9650, UC
// 53463101207). Era o outro lado do bug de 10/08: antes do fix a emissão dela
// caía silenciosamente no Sicoob e cobrava na conta da Vigor; depois do fix ia
// para a rota certa e esbarrava nesta trava.
//
// `empresa_parceira_id IS NULL` é o que separa time interno de usuário de
// parceira: o departamento 'Financeiro' também tem empresa_parceira_admin da
// própria Gran, e para esses continua valendo só o isolamento por empresa.

export const DEPARTAMENTOS_EMISSAO_INTERNA = ["Faturamento", "Financeiro"];

export interface PerfilEmissor {
    role?: string | null;
    departamento?: string | null;
    empresa_parceira_id?: string | null;
    full_name?: string | null;
    permissions?: string[] | null;
}

/**
 * Permissão nominal de quem faz a cobrança (PermissionsModal). Libera o boleto
 * avulso de cliente PRÓPRIO da parceira, fora da parceria com a Vigor — e só ele:
 * não abre o resto da cobrança da parceira. Pedido de 22/09/2026 para a Dhaiane,
 * que é do Pós Venda e ficava de fora da faixa Faturamento/Financeiro.
 */
export const PERMISSAO_COBRANCA = "Realizar Cobrança / Negociação";

export function ehAdm(perfil: PerfilEmissor | null | undefined): boolean {
    return perfil?.role === "adm";
}

/** Time interno da Vigor que fatura/cobra — nunca um usuário de parceira. */
export function ehInternoFaturamento(perfil: PerfilEmissor | null | undefined): boolean {
    if (!perfil || perfil.empresa_parceira_id) return false;
    return DEPARTAMENTOS_EMISSAO_INTERNA.includes(perfil.departamento ?? "");
}

export function podeOperarCobrancaDaParceira(
    perfil: PerfilEmissor | null | undefined,
    empresaParceiraIdDaFatura: string | null | undefined,
): boolean {
    if (!perfil) return false;
    if (ehAdm(perfil) || ehInternoFaturamento(perfil)) return true;
    return !!empresaParceiraIdDaFatura && empresaParceiraIdDaFatura === perfil.empresa_parceira_id;
}

/**
 * Nome para carimbar no histórico de cancelamento. O resto do app grava o nome
 * real de quem clicou (`profile.full_name`) — os rótulos genéricos são só
 * fallback para perfil sem nome preenchido.
 */
export function nomeDoOperador(perfil: PerfilEmissor | null | undefined): string {
    const nome = perfil?.full_name?.trim();
    if (nome) return nome;
    if (ehAdm(perfil)) return "Admin";
    if (ehInternoFaturamento(perfil)) return "Vigor Energy";
    return "Empresa Parceira";
}

/**
 * Quem pode emitir/cancelar/prorrogar o boleto avulso de cliente próprio da
 * parceira (`faturas.fora_parceria_vigor`): quem já opera a cobrança da parceira
 * como time interno, mais quem tem a permissão de cobrança. Usuário de parceira
 * fica de fora — esta tela é da operação da Vigor.
 *
 * Espelhada pela RPC `parceiras_boleto_avulso` (migration 20260922205457), que é
 * quem entrega à tela a lista de parceiras para quem não lê a tabela pelo RLS.
 */
export function podeEmitirBoletoForaParceria(perfil: PerfilEmissor | null | undefined): boolean {
    if (!perfil || perfil.empresa_parceira_id) return false;
    if (ehAdm(perfil) || ehInternoFaturamento(perfil)) return true;
    return Array.isArray(perfil.permissions) && perfil.permissions.includes(PERMISSAO_COBRANCA);
}
